import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useWebsocket from '../hooks/websocketHook.js';
import { ensureArray } from "../utils/arrayUtilities.js";
import useClientConfig from "./clientConfigHook";
import useNetwork, { QueryContext } from "./networkHook";
import { DialogManagementProvider, Modal } from "../utils/dialogUtlities.js";
import { fetchedOnAuth, fetchedOnReady, mountedInstances, unlisten } from "../reducers/initContext.js";
import { useNetworkState } from "./appStateHook.js";

const GET_SHOW_TIMEOUT = 250;
const SHOW_TIMEOUT = 500;
const HIDE_TIMEOUT = 750;

export const MIN_TIMEOUT = Math.min(GET_SHOW_TIMEOUT, SHOW_TIMEOUT, HIDE_TIMEOUT);

export const NetworkStatusMonitor = ({ children }) => {
    const { t } = useTranslation();
    const [longLoad, setLongLoad] = useState(false);
    const activeQueries = useRef({});
    const callbacks = useRef([]);
    const hideTimeoutRef = useRef();

    const { isOnline, wsStatus } = useNetworkState();

    useEffect(() => {
        if (wsStatus !== 'authenticated') {
            fetchedOnAuth.forEach((_, run) => fetchedOnAuth.set(run, false));
        }
    }, [wsStatus]);

    useEffect(() => {
        if (!isOnline) {
            fetchedOnAuth.forEach((_, run) => fetchedOnAuth.set(run, false));
            fetchedOnReady.forEach((_, run) => fetchedOnAuth.set(run, false));
        }
    }, [isOnline]);

    useEffect(() => {
        return () => {
            Object.keys(activeQueries.current).forEach(timeoutId => clearTimeout(timeoutId));
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef);
            }
        };
    }, []);

    const isLongLoad = useCallback(() => Object.values(activeQueries.current).some(q => q.timeoutFired), []);

    const updateLongLoadState = useCallback(() => {
        clearTimeout(hideTimeoutRef.current);  // Clear any existing timeout
        if (isLongLoad()) {
            if (callbacks.current.length > 0) {
                callbacks.current.forEach(c => c(true));
            } else {
                setLongLoad(true);
            }
        } else {
            // Set a timeout to hide longLoad, allowing for debouncing
            hideTimeoutRef.current = setTimeout(() => {
                if (callbacks.current.length > 0) {
                    callbacks.current.forEach(c => c(false));
                } else {
                    setLongLoad(false);
                }
            }, HIDE_TIMEOUT);
        }
    }, [isLongLoad]);

    const beginQuery = useCallback((metadata = {}) => {
        const queryID = crypto.randomUUID();
        // Create a timeout and store its ID as the lookup key
        const timeoutId = setTimeout(() => {
            activeQueries.current[queryID].timeoutFired = true;
            updateLongLoadState();
        }, metadata.method === 'GET' ? GET_SHOW_TIMEOUT : SHOW_TIMEOUT);

        // Store the query immediately without marking it as long-running
        activeQueries.current[queryID] = { timeoutId, metadata, timeoutFired: false };
        return queryID;
    }, [updateLongLoadState]);

    const endQuery = useCallback((queryID) => {
        if (activeQueries.current[queryID]) {
            clearTimeout(activeQueries.current[queryID].timeoutId);
            delete activeQueries.current[queryID];
            updateLongLoadState()
        }
    }, [updateLongLoadState]);

    const addLoadingCallback = useCallback((callback) => {
        if (!callbacks.current.includes(callback)) {
            callbacks.current.push(callback);
            setLongLoad(false);
            callback(isLongLoad());
        }
    }, [isLongLoad]);

    const removeLoadingCallback = useCallback((callback) => {
        const idx = callbacks.current.findIndex(c => c === callback);
        if (idx > -1) {
            callbacks.current.splice(idx, 1);
            if (callbacks.current.length === 0) {
                setLongLoad(isLongLoad());
            }
        }
    }, [isLongLoad]);

    const value = useMemo(() => ({ beginQuery, endQuery, addLoadingCallback, removeLoadingCallback }), [beginQuery, endQuery, addLoadingCallback, removeLoadingCallback]);

    return <DialogManagementProvider>
        <QueryContext.Provider value={value}>
            <Modal show={false && longLoad} centered>
                <Modal.Body>{t('loadPending')}</Modal.Body>
            </Modal>
            {children}
        </QueryContext.Provider>
    </DialogManagementProvider>
}

/**
 * This is a utility function to help reduce the amount of refetching when running realization - it allows for a scoped marking
 * of run functions as fetched wand the handling of listener registration and cleanup
 * @param {*} realizationContext a base realization context to which the scope should be added
 * @param {*} continuation the code to run in the realization scope
 */
export const withRealizationScope = async (realizationContext, continuation) => {
    if (realizationContext.isFetched && realizationContext.markFetched && realizationContext.doListen) {
        return await continuation(realizationContext);
    } else {
        const realizedFetches = new Set();
        const cleanup = [];

        const isFetched = run => realizedFetches.has(run);
        const markFetched = run => realizedFetches.add(run);
        const doListen = listen => cleanup.push(...Object.entries(listen).map(([ event, callback ]) => realizationContext.websocket.register(event, callback)));
        
        const result = await continuation({ ...realizationContext, isFetched, markFetched, doListen });

        cleanup.forEach(c => c());

        return result;
    }
}

/**
 * This is a utility function which allows us to use the data hook infrastructure to await a value to be read either from the redux (if currently available and live
 * according to the data hook infrastructure) or fetched.
 * @param {*} clientConfig the clientConfig
 * @param {*} network the network object
 * @param {*} options options corresponding with those of the data hook - cleanup is unused as no results fetched are marked as 'fetched' in the infrastructure
 */
export async function realize({ clientConfig, network, isFetched, markFetched, doListen }, { deps, run, listen, authenticated = true, immutable = false }) {
    const areDepsMet = deps === undefined || deps === true || (typeof deps === 'function' && clientConfig && deps(clientConfig)) || (Array.isArray(deps) && deps.every(d => !!d));
    const fetchMap = authenticated ? fetchedOnAuth : fetchedOnReady
    if (!areDepsMet) {
        throw new Error('Deps are not met and we are doing a synchronous init');
    }
    // fetches can be marked in the core infrastructure from existing hooks etc (the fetchMap) or, if present, in the context of a
    // realization scope (from withRealizationScope above). If we have a realization scope, then we listen, fetch, mark as fetched
    // in the scope and (when the scope concludes) cleanup the listeners. If there is no realization scope then we mark nothing fetched
    // unless the entity is immutable in which case we can mark it as fetched and fetch safely.
    if (!immutable) {
        if (!fetchMap.get(run) && !isFetched?.(run)) {
            doListen?.(listen);
            await Promise.allSettled(ensureArray(run).map(r => r(network, r => markFetched?.(r))));
            ensureArray(run).forEach(r => markFetched?.(r));
        }
    } else if (!fetchMap.get(run)) {
        await Promise.allSettled(ensureArray(run).map(r => {
            fetchMap.set(r, true);
            return r(network)
        }));
    }
}

/**
 * This hook leverages the infrastructure populated by the useInit hook. This hook will read data from the data hook infrastructure, but
 * will only do so when the run function which would be used to fetch the data is marked as live by the use of the run by a call to useInit.
 * Note: any use of hooks derrived from useReadonly need to ensure they are properly scoped in the render hierarchy such that the run function
 * will be held live beyond the lifetime of the use of this hook.
 */
export function useReadonly({ run, deps, name, authenticated = true }) {
    // this check is only valid when run outside of an ordered hook factory - not supported for now but left commented out for debugging until supported
    /*const clientConfig = useClientConfig();
    const { isOnline, wsStatus } = useNetworkState();
    const areDepsMet = useMemo(() => deps === undefined || deps === true || (typeof deps === 'function' && clientConfig && deps(clientConfig)) || (Array.isArray(deps) && deps.every(d => !!d)), [deps, clientConfig]);

    useEffect(() => {
        if (!areDepsMet) {
            return;
        }
        if (!run) {
            return;
        }
        const runnable = authenticated ? wsStatus === 'authenticated' : isOnline;
        ensureArray(run).forEach(run => {
            if (runnable && !mountedInstances.has(run)) {
                throw new Error(`useReadonly detected run function ${name} is not mounted!`);
            }
        })
    }, [areDepsMet, wsStatus, authenticated, isOnline, run, name]);*/
}

// this map holds init factories for each entity kind that has been registered so that they can mark
// the appropriate run as fetched when we are notified of a create to prevent network traffic to get
// empty results or data we already have
const entityInitRegistry = new Map();

/**
 * When we are notified of a create via the websocket we need to make sure that the data layer does not go and fetch that entity just because
 * it was created, or worse, fetch a sub-entity (eg a note for a member) just because the member was created. This function
 * allows us to register identity-base init factories against kinds so that we can mark the fetches as already fetched when
 * a new value is created.
 * @param {*} entity A string that represents the kind of entity the init factory is being registered against
 * @param {*} initMapFactory The factory to call to build the init needed to mark a newly created entity as fetched
 */
export const registerEntityBasedFetch = (entity, initMapFactory) => {
    if (entityInitRegistry.has(entity)) {
        entityInitRegistry.set(entity, [ ...entityInitRegistry.get(entity), initMapFactory ]);
    } else {
        entityInitRegistry.set(entity, [initMapFactory]);
    }
}

/**
 * This helper is called from a create listener to handle marking fetches based on the identity of a newly created object as having already
 * been fetched. We can only do this safely when there is a listener registered to listen for upcoming changes.
 * @param {*} websocket The websocket provider - needed to ensure listeners are listening
 * @param {*} sourceEntity The kind of the entity that was created (used as the lookup into the registry above)
 * @param  {...any} args The primary key of the entity created - accepts multiple values for compound primary keys
 */
export const markFetched = (websocket, sourceEntity, ...args) => {
    if (entityInitRegistry.has(sourceEntity)) {
        for (const init of entityInitRegistry.get(sourceEntity)) {
            const { run, listen, authenticated } = args.length === 0 ? init : init(...args);
            if (Object.entries(listen).every(([ event, callback ]) => websocket.isRegistered(event, callback))) {
                const fetchMap = authenticated ? fetchedOnAuth : fetchedOnReady;
                ensureArray(run).forEach(run => fetchMap.set(run, true));
            }
        }
    }
}

/**
 * This hook is the engine of the data hook infrastructure and takes care of tracking run function liveness,
 * fetching data when new run functions are used that are not currently initialized, and cleaning up the redux
 * when the last component using a given run function unmounts.
 */
export function useInit({ run, listen, deps, name, authenticated = true, immutable = false }, immortal) {
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const { register } = useWebsocket();
    const { wsStatus, isOnline } = useNetworkState();
    const areDepsMet = useMemo(() => deps === undefined || deps === true || (typeof deps === 'function' && clientConfig && deps(clientConfig)) || (Array.isArray(deps) && deps.every(d => !!d)), [deps, clientConfig]);
    const derrived = useRef([]);
    const fetchMap = authenticated ? fetchedOnAuth : fetchedOnReady
    const isOnlineGuard = useRef(isOnline);

    // we don't wan tthe useEffect that includes cleanup to run just because the socket status has changed, but we must have
    // the current socket status at the point where we consider cleanup to ensure that we don't purge data while offline
    useEffect(() => {
        isOnlineGuard.current = isOnline;
    }, [isOnline]);

    useEffect(() => {
        if (!areDepsMet) {
            return;
        }
        if (!run) {
            return;
        }

        ensureArray(run).forEach(run => {
            if (!mountedInstances.has(run)) {
                mountedInstances.set(run, 0);
                if (!fetchMap.has(run)) {
                    fetchMap.set(run, false);
                }
            }
        })

        const cleanupFuncs = ensureArray(run).map(run => {
            const currentCount = mountedInstances.inc(run);
            
            if (currentCount === 1) {
                if (listen && !unlisten.has(run)) {
                    const cleanups = Object.entries(listen).map(([ event, callback ]) => register(event, callback));
                    const cleanup = () => cleanups.forEach(c => c());
                    unlisten.set(run, cleanup);
                }
            }

            return () => {
                const updatedCount = mountedInstances.dec(run);
                
                const derrivedToUnmark = [...derrived.current];
                const cleanupRequired = derrivedToUnmark.map(r => mountedInstances.dec(r)).some(count => count === 0) || updatedCount === 0;
                derrived.current = [];

                if (cleanupRequired && !immortal && isOnlineGuard.current) {
                    // we want to delay the cleanup in case we are unmounting and remounting the same components
                    // we must check the given run hasn't remounted after the timeout before doing the cleanup
                    setTimeout(() => {
                        [run, ...derrivedToUnmark].forEach(r => {
                            if (mountedInstances.get(r) === 0) {
                                if (unlisten.has(r)) {
                                    unlisten.get(r)();
                                    unlisten.delete(r);
                                }
                                fetchMap.set(r, false);
                            }
                        });
                    }, 1000)
                }
            }
        })

        return () => {
            cleanupFuncs.forEach(c => c());
        };
    }, [run, areDepsMet, listen, register, immortal, fetchMap]);

    useEffect(() => {
        if (!run || !areDepsMet) {
            ensureArray(run).forEach(run => fetchMap.set(run, false));
        } else {
            ensureArray(run).forEach(run => {
                const fetched = fetchMap.get(run);
                const runnable = authenticated ? wsStatus === 'authenticated' : isOnline;
                if (runnable && !fetched) {
                    fetchMap.set(run, true);
                    derrived.current.forEach(r => mountedInstances.dec(r));
                    derrived.current = [];
                    run(network, r => {
                        fetchMap.set(r, true);
                        mountedInstances.inc(r);
                        derrived.current.push(r);
                    })
                }

                if (!runnable && fetched && !immutable) {
                    fetchMap.set(run, false);
                    derrived.current.forEach(r => mountedInstances.dec(r));
                    derrived.current = [];
                }
            })
        }

    }, [wsStatus, areDepsMet, network, run, isOnline, authenticated, name, fetchMap, immutable])
}

